// ═══════════════════════════════════════════════════════════════
// Correo de restablecer la contraseña (firebaseLocale.js)
//
// sendPasswordReset({ auth, email, lang, send }) lo pide primero al Worker
// (POST /api/auth-password-reset): Firebase genera el enlace sin mandar nada
// y el Worker manda el correo por Resend, desde nuestro remitente, con el
// diseño aprobado y en el idioma del usuario. Si el Worker no responde bien
// (sin red, 429, 502, o un Worker anterior sin la ruta), se pide a Firebase
// como antes con `send`, que manda su plantilla de fábrica.
//
// Firebase manda la suya en el idioma que lleve `auth.languageCode` al
// pedirla (la página del enlace sale en ese mismo idioma); sin él, en el del
// proyecto, que es inglés. withLocale(auth, idioma, enviar) pone el idioma,
// hace el envío y deja `languageCode` como estaba cuando el envío termina
// (no antes: el SDK lee el idioma después de un `await`).
//
// Sin idioma, el de la app (el que eligió el usuario en TikControl). Un
// idioma que la app no tiene → el del proyecto.
//
// Fuente única: la carga la app (antes de passwordSecurityPrompt.js y
// auth.js) y el backoffice (Users.jsx la importa tal cual). Sin DOM.
// Expone TCFirebaseLocale.
// TikControl © 2024-2026
// ═══════════════════════════════════════════════════════════════

(function (root) {
  'use strict';

  // Los 14 idiomas de la app (renderer/i18n/locales).
  var APP_LANGS = ['es', 'en', 'fr', 'de', 'it', 'pt', 'ru', 'ja', 'ko', 'zh', 'ar', 'hi', 'tr', 'id'];

  // Código que entiende Firebase. El portugués de la app es el de Brasil y el
  // chino, el simplificado: Firebase los quiere con su variante.
  function localeFor(value) {
    var raw = String(value || '').trim().toLowerCase().replace(/_/g, '-');
    var base = raw.split('-')[0];
    if (APP_LANGS.indexOf(base) === -1) return null;
    if (base === 'pt') return raw === 'pt-pt' ? 'pt-PT' : 'pt-BR';
    if (base === 'zh') return /^zh-(tw|hk|mo|hant)/.test(raw) ? 'zh-TW' : 'zh-CN';
    return base;
  }

  function appLanguage() {
    try {
      var i18n = root.i18n;
      return i18n && typeof i18n.getCurrentLanguage === 'function' ? i18n.getCurrentLanguage() : '';
    } catch (_) {
      return '';
    }
  }

  function withLocale(auth, lang, send) {
    var code = localeFor(lang === undefined ? appLanguage() : lang);
    var before = null;
    try {
      before = auth.languageCode == null ? null : auth.languageCode;
      auth.languageCode = code;
    } catch (_) {}
    function restore() {
      try { auth.languageCode = before; } catch (_) {}
    }
    var out;
    try {
      out = send();
    } catch (e) {
      restore();
      throw e;
    }
    return Promise.resolve(out).then(
      function (value) { restore(); return value; },
      function (err) { restore(); throw err; }
    );
  }

  var RESET_URL = 'https://ws.tikcontrol.live/api/auth-password-reset';
  var RESET_TIMEOUT_MS = 10000;

  // Responde { via: 'worker' | 'firebase', lang }; solo falla si fallan los dos.
  function sendPasswordReset(opts) {
    var auth = opts.auth;
    var lang = opts.lang === undefined ? appLanguage() : opts.lang;
    var base = (localeFor(lang) || '').split('-')[0];
    var firebase = function () {
      return withLocale(auth, lang, opts.send).then(function () {
        return { via: 'firebase', lang: localeFor(lang) || 'en' };
      });
    };
    var fetchImpl = opts.fetch || root.fetch;
    if (typeof fetchImpl !== 'function') return firebase();

    var controller = typeof root.AbortController === 'function' ? new root.AbortController() : null;
    var timer = controller ? setTimeout(function () { controller.abort(); }, RESET_TIMEOUT_MS) : null;
    var request;
    try {
      request = Promise.resolve(fetchImpl(RESET_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: opts.email, lang: base }),
        signal: controller ? controller.signal : undefined
      }));
    } catch (e) {
      request = Promise.reject(e);
    }
    return request.then(function (res) {
      if (!res || !res.ok) return null;
      return res.json().catch(function () { return null; });
    }).catch(function () {
      return null;
    }).then(function (out) {
      if (timer) clearTimeout(timer);
      if (out && out.ok === true) return { via: 'worker', lang: base || 'en' };
      return firebase();
    });
  }

  root.TCFirebaseLocale = {
    APP_LANGS: APP_LANGS,
    RESET_URL: RESET_URL,
    localeFor: localeFor,
    appLanguage: appLanguage,
    withLocale: withLocale,
    sendPasswordReset: sendPasswordReset
  };
})(typeof globalThis !== 'undefined' ? globalThis : this);
